import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { getLivePublicMatches } from "../store/supabaseMatches";
import { getPublicUpcomingFixtures } from "../store/publicLeagues";
import { supabase } from "../supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import PublicLayout from "../components/public/PublicLayout";
import { type PublicMatch } from "../components/public/MatchCard";
import { navigate } from "../utils/router";
import watchwicketLogo from "../assets/file_00000000711072438e9d72dabae9eda2.png";

function isAbortError(err: unknown): boolean {
  return err instanceof Error && (err.name === "AbortError" || err.message === "signal is aborted without reason");
}

function formatScheduledLabel(dateStr: string | null | undefined, timeOverride?: string | null): string {
  if (!dateStr) return timeOverride || "TBD";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return timeOverride || "TBD";
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const timeStr = timeOverride
    ? timeOverride
    : (d.getHours() !== 0 || d.getMinutes() !== 0)
      ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "";
  if (isToday) return timeStr ? `Today ${timeStr}` : "Today";
  if (isTomorrow) return timeStr ? `Tomorrow ${timeStr}` : "Tomorrow";
  const dateLabel = d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
  return timeStr ? `${dateLabel} ${timeStr}` : dateLabel;
}

function formatUKDate(dateStr: string | null | undefined, timeOverride?: string | null): { dateLine: string; timeLine: string } {
  if (!dateStr) return { dateLine: timeOverride || "Date TBD", timeLine: "" };
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return { dateLine: timeOverride || "Date TBD", timeLine: "" };

  const dateLine = d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const timeLine = timeOverride
    ? timeOverride
    : (d.getHours() !== 0 || d.getMinutes() !== 0)
      ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
      : "";

  return { dateLine, timeLine };
}

function getTeamLogos(match: PublicMatch): { logoA: string | null; logoB: string | null } {
  const md = match.matchData as any;
  return {
    logoA: md?.teamALogo ?? null,
    logoB: md?.teamBLogo ?? null,
  };
}

function getResultSentence(match: PublicMatch): string {
  if (match.result) return match.result;
  const md = match.matchData as any;
  if (md?.result) return md.result;
  const innings = md?.innings;
  if (!innings || innings.length < 2) return "";
  const i1 = innings[0];
  const i2 = innings[1];
  const a = i1?.totalRuns ?? 0;
  const b = i2?.totalRuns ?? 0;
  if (a === b) return "Match tied";
  const diff = Math.abs(a - b);
  if (a > b) {
    return `${match.teamA} won by ${diff} run${diff !== 1 ? "s" : ""}`;
  }
  const wktsRemaining = 10 - (i2?.wickets ?? 0);
  if (wktsRemaining > 0) {
    return `${match.teamB} won by ${wktsRemaining} wicket${wktsRemaining !== 1 ? "s" : ""}`;
  }
  return `${match.teamB} won by ${diff} run${diff !== 1 ? "s" : ""}`;
}

export default function PublicHomePage() {
  const [matches, setMatches] = useState<PublicMatch[]>([]);
  const [leagueFixtures, setLeagueFixtures] = useState<PublicMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const mountedRef = useRef(true);

  const fetchMatches = useCallback(async () => {
    try {
      const [matchData, fixtureData] = await Promise.all([
        getLivePublicMatches(),
        getPublicUpcomingFixtures(),
      ]);
      if (!mountedRef.current) return;
      setMatches(matchData || []);

      const matchIds = new Set((matchData || []).map((m) => m.id));

      const transformed: PublicMatch[] = (fixtureData || [])
        .filter((f) => !f.match_id || !matchIds.has(f.match_id))
        .filter((f) => {
          const s = (f.status || "").toLowerCase();
          return s !== "completed" && s !== "cancelled" && s !== "postponed";
        })
        .map((f) => {
          const rawDate = f.scheduled_at || f.match_date || null;
          const startTimeLabel = formatScheduledLabel(rawDate, f.match_time);
          return {
            id: f.id,
            teamA: f.team_a_name || f.home_team_name || "TBD",
            teamB: f.team_b_name || f.away_team_name || "TBD",
            status: "upcoming" as const,
            format: (f as any).league_name || "League Match",
            startTime: startTimeLabel || "TBD",
            scheduledAt: rawDate,
            venue: f.venue || undefined,
          };
        });

      setLeagueFixtures(transformed);
    } catch (err) {
      if (!mountedRef.current) return;
      if (isAbortError(err)) return;
      console.error("[PublicHomePage] Error fetching matches:", err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    document.body.classList.add("watchwicket-homepage");

    if (user) {
      navigate("/scorebox");
      return;
    }

    fetchMatches();

    const channel = supabase
      .channel("public-homepage-matches")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "matches", filter: "is_public=eq.true" },
        () => {
          if (!mountedRef.current) return;
          fetchMatches();
        }
      )
      .subscribe();

    channelRef.current = channel;

    const pollInterval = setInterval(() => {
      if (!mountedRef.current) return;
      fetchMatches();
    }, 3000);

    return () => {
      mountedRef.current = false;
      document.body.classList.remove("watchwicket-homepage");
      clearInterval(pollInterval);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user, fetchMatches]);

  const liveMatches = useMemo(() => matches.filter((m) => m.status === "live"), [matches]);
  const liveMatchIds = useMemo(() => new Set(liveMatches.map((m) => m.id)), [liveMatches]);
  const matchUpcoming = useMemo(() => matches.filter((m) => m.status === "upcoming"), [matches]);

  const upcomingMatches = useMemo(() => {
    const allUpcoming = [...matchUpcoming, ...leagueFixtures]
      .filter((m) => !liveMatchIds.has(m.id))
      .sort((a, b) => {
        const aDate = (a as any).scheduledAt;
        const bDate = (b as any).scheduledAt;
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return new Date(aDate).getTime() - new Date(bDate).getTime();
      });
    return allUpcoming.slice(0, 3);
  }, [matchUpcoming, leagueFixtures, liveMatchIds]);

  const recentResults = useMemo(
    () => matches.filter((m) => m.status === "completed").slice(0, 3),
    [matches]
  );

  const hasLive = liveMatches.length > 0;
  const hasUpcoming = upcomingMatches.length > 0;
  const hasResults = recentResults.length > 0;

  return (
    <PublicLayout activePath="/" showBottomNav>
      {/* HERO */}
      <HeroSection />

      {/* DYNAMIC SECTIONS */}
      <div className="relative z-10 w-full max-w-3xl mx-auto px-4 pt-5 pb-8 space-y-8">
        {loading ? (
          <div className="space-y-8">
            <div className="space-y-3">
              <div className="h-5 w-28 rounded-lg bg-white/[0.06] animate-pulse" />
              <div className="h-40 rounded-2xl bg-white/[0.04] animate-pulse" />
            </div>
            <div className="space-y-3">
              <div className="h-5 w-28 rounded-lg bg-white/[0.06] animate-pulse" />
              <div className="h-40 rounded-2xl bg-white/[0.04] animate-pulse" />
            </div>
          </div>
        ) : (
          <>
            {/* LIVE */}
            {hasLive && (
              <Section
                title="Live Matches"
                accent="live"
                matches={liveMatches.slice(0, 3)}
                cardType="live"
                onViewAll={liveMatches.length > 3 ? () => navigate("/live") : undefined}
              />
            )}

            {/* UPCOMING */}
            {hasUpcoming && (
              <Section
                title="Upcoming Matches"
                accent="upcoming"
                matches={upcomingMatches}
                cardType="upcoming"
                onViewAll={() => navigate("/fixtures")}
              />
            )}

            {/* RESULTS */}
            {hasResults && (
              <Section
                title="Recent Results"
                accent="results"
                matches={recentResults}
                cardType="results"
                onViewAll={() => navigate("/results")}
              />
            )}

            {/* EMPTY STATE — no matches at all */}
            {!hasLive && !hasUpcoming && !hasResults && (
              <div className="rounded-2xl border border-white/[0.06] p-8 text-center" style={{ background: "rgba(8,39,29,0.5)" }}>
                <p className="text-[#9FB6AC] font-semibold text-sm mb-1">No matches available</p>
                <p className="text-[#708A80] text-xs">Check back soon for live scores and fixtures.</p>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes ph-pulse-live { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes ph-fade-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        @media (prefers-reduced-motion: reduce) {
          .ph-anim-fade-up { animation: none !important; opacity: 1 !important; }
          .ph-live-pulse { animation: none !important; }
        }
      `}</style>
    </PublicLayout>
  );
}


/* ─── HERO ─── */

function HeroSection() {
  const quickLinks = [
    { label: "Teams", path: "/teams", icon: "teams" },
    { label: "Leagues", path: "/leagues", icon: "leagues" },
    { label: "Fixtures", path: "/fixtures", icon: "fixtures" },
    { label: "Results", path: "/results", icon: "results" },
  ];

  return (
    <section className="relative overflow-hidden border-b border-[#00C980]/10">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_12%,rgba(0,201,128,0.28),transparent_42%),linear-gradient(180deg,#053D2A_0%,#042A1E_48%,#031B13_100%)]" />
      <div className="absolute inset-x-0 top-0 h-60 opacity-80" style={{ background: "repeating-linear-gradient(115deg, transparent 0 26px, rgba(175,255,218,0.04) 27px 30px, transparent 31px 72px)" }} />
      <div className="absolute left-[-16%] top-24 h-40 w-52 rounded-full bg-[#E8FFF4]/12 blur-3xl" />
      <div className="absolute right-[-12%] top-16 h-48 w-56 rounded-full bg-[#E8FFF4]/14 blur-3xl" />
      <div className="absolute inset-x-0 bottom-0 h-44 opacity-25" style={{ background: "radial-gradient(ellipse at 50% 100%, #00C980 0%, transparent 68%)" }} />

      <div className="relative z-10 w-full px-4 pt-0 pb-5 sm:pt-1 sm:pb-6 ph-anim-fade-up" style={{ animation: "ph-fade-up 0.6s ease-out" }}>
        <div className="relative text-center">
          <div className="absolute right-0 top-0 text-right text-[#E7FFF4]" style={{ fontFamily: "cursive", fontSize: "clamp(17px, 5vw, 25px)", lineHeight: 1.02, transform: "rotate(-7deg)" }}>
            <span className="block">Cricket</span>
            <span className="block">Brings</span>
            <span className="block relative">People Together<span className="absolute -bottom-2 right-0 w-24 h-1 border-b-2 border-[#00C980] rounded-[50%]" /></span>
          </div>

          <img src={watchwicketLogo} alt="WatchWicket" className="mx-auto w-80 h-40 sm:w-[28rem] sm:h-52 object-contain drop-shadow-[0_10px_28px_rgba(0,0,0,0.42)]" />
          <p className="text-[10px] sm:text-xs font-bold text-[#C5E8D9] tracking-[0.34em] uppercase -mt-2">More than a game</p>

          <h1 className="text-3xl sm:text-4xl font-black text-[#F7FAF8] leading-[1.03] mt-3">
            Live Cricket.<br /><span className="text-[#19E69B]">Anytime. Anywhere.</span>
          </h1>
          <p className="text-[#E0F1E9] text-sm sm:text-base leading-relaxed max-w-lg mx-auto mt-3">
            Live scores, upcoming matches, results and competitions — all in one place.
          </p>
          <p className="text-[#F1FFF8] text-[10px] sm:text-xs font-bold tracking-[0.28em] uppercase mt-5">Every ball. Every run. Every wicket.</p>
          <div className="mx-auto mt-3 h-1 w-24 rounded-full bg-[#00C980]" />

          <div className="grid grid-cols-4 gap-2 sm:gap-3 mt-6">
            {quickLinks.map((link) => (
              <button key={link.path} onClick={() => navigate(link.path)} className="rounded-2xl border border-[#00C980]/55 bg-[#062F22]/55 px-1.5 py-3 sm:py-4 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition-all hover:bg-[#00C980]/15 active:scale-95">
                <span className="flex justify-center text-[#F7FAF8] mb-2" aria-hidden="true"><QuickLinkIcon kind={link.icon} /></span>
                <span className="text-[9px] sm:text-xs font-bold uppercase tracking-wide text-[#E7FFF4]">{link.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function QuickLinkIcon({ kind }: { kind: string }) {
  const common = { className: "w-7 h-7", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", strokeWidth: 2 };
  if (kind === "teams") return <svg {...common}><path strokeLinecap="round" strokeLinejoin="round" d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zm12 10v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>;
  if (kind === "leagues") return <svg {...common}><path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8m-4-4v4M5 3h14v5a7 7 0 01-14 0V3zM5 5H2v2a5 5 0 005 5m12-7h3v2a5 5 0 01-5 5" /></svg>;
  if (kind === "fixtures") return <svg {...common}><path strokeLinecap="round" strokeLinejoin="round" d="M8 3v4m8-4v4M4 10h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" /></svg>;
  return <svg {...common}><path strokeLinecap="round" strokeLinejoin="round" d="M4 19V9m5 10V5m5 14v-7m5 7V3" /></svg>;
}


/* ─── SECTION + CAROUSEL ─── */

interface SectionProps {
  title: string;
  accent: "live" | "upcoming" | "results";
  matches: PublicMatch[];
  cardType: "live" | "upcoming" | "results";
  onViewAll?: () => void;
}

function Section({ title, accent, matches, cardType, onViewAll }: SectionProps) {
  const accentColors = {
    live: "#FF3B3B",
    upcoming: "#45B8FF",
    results: "#D6B85A",
  };
  const color = accentColors[accent];

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          {accent === "live" && (
            <div
              className="w-2 h-2 rounded-full ph-live-pulse"
              style={{ backgroundColor: color, animation: "ph-pulse-live 1.5s ease-in-out infinite" }}
            />
          )}
          <h2 className="text-sm font-black text-[#F7FAF8] uppercase tracking-wider">{title}</h2>
          <div className="w-8 h-[2px] rounded-full" style={{ backgroundColor: color, opacity: 0.4 }} />
        </div>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="flex items-center gap-1 text-[#00C980] hover:text-[#19E69B] text-xs font-semibold transition-colors"
          >
            View All {accent === "live" ? "Live" : accent === "upcoming" ? "Upcoming" : "Results"}
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      <Carousel matches={matches} cardType={cardType} />
    </section>
  );
}


/* ─── CAROUSEL (CSS scroll-snap, max 3, no infinite) ─── */

function Carousel({ matches, cardType }: { matches: PublicMatch[]; cardType: "live" | "upcoming" | "results" }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.clientWidth;
    const idx = Math.round(el.scrollLeft / cardWidth);
    setActiveIndex(Math.max(0, Math.min(matches.length - 1, idx)));
  }, [matches.length]);

  useEffect(() => {
    setActiveIndex(0);
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, behavior: "auto" });
    }
  }, [matches.length]);

  return (
    <div>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 pb-1"
        style={{ scrollSnapType: "x mandatory" }}
      >
        {matches.map((match) => (
          <div
            key={match.id}
            className="snap-center flex-shrink-0 w-full"
            style={{ scrollSnapAlign: "center", scrollSnapStop: "always" }}
          >
            {cardType === "live" && <LiveCard match={match} />}
            {cardType === "upcoming" && <UpcomingCard match={match} />}
            {cardType === "results" && <ResultCard match={match} />}
          </div>
        ))}
      </div>

      {/* Pagination dots — only when 2+ */}
      {matches.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3">
          {matches.map((_, idx) => (
            <div
              key={idx}
              className="rounded-full transition-all duration-200"
              style={{
                width: idx === activeIndex ? 20 : 6,
                height: 6,
                backgroundColor: idx === activeIndex ? "#00C980" : "rgba(255,255,255,0.15)",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}


/* ─── TEAM LOGO ─── */

function TeamLogo({ logo, name, size = 48 }: { logo: string | null; name: string | null; size?: number }) {
  const safeName = name ?? "TBD";
  const initials = safeName.slice(0, 2).toUpperCase();
  return (
    <div
      className="flex items-center justify-center flex-shrink-0 overflow-hidden rounded-2xl border border-[#00C980]/20"
      style={{
        width: size,
        height: size,
        background: logo ? "rgba(10,55,39,0.55)" : "linear-gradient(135deg, #0C5137 0%, #062A1E 100%)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
      }}
    >
      {logo ? (
        <img src={logo} alt={safeName} className="w-full h-full object-contain p-1.5" />
      ) : (
        <span className="text-2xl sm:text-3xl font-black tracking-tight text-[#19E69B]">{initials}</span>
      )}
    </div>
  );
}


/* ─── LIVE CARD ─── */

function LiveCard({ match }: { match: PublicMatch }) {
  const { logoA, logoB } = getTeamLogos(match);

  return (
    <button
      onClick={() => navigate(`/match/${match.id}`)}
      className="text-left w-full rounded-2xl p-4 sm:p-5 transition-all duration-200 active:scale-[0.985]"
      style={{
        background: "rgba(6, 34, 25, 0.9)",
        border: "1px solid rgba(0,201,128,0.3)",
        boxShadow: "0 2px 12px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)",
        backdropFilter: "blur(12px)",
      }}
    >
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-1.5 bg-[#FF3B3B]/15 border border-[#FF3B3B]/20 rounded-full px-2.5 py-1">
          <div className="w-1.5 h-1.5 rounded-full bg-[#FF3B3B] ph-live-pulse" style={{ animation: "ph-pulse-live 1.5s ease-in-out infinite" }} />
          <span className="text-[#FF3B3B] text-[10px] font-bold uppercase tracking-wider">Live</span>
        </div>
        <span className="text-[#B8D4C8] text-[11px] font-bold tracking-wider uppercase rounded-full bg-white/[0.05] px-2.5 py-1">{match.format || "Live match"}</span>
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2">
        <LiveTeam name={match.teamA} logo={logoA} score={match.scoreA || "-"} overs={match.overs ? `(${match.overs} overs)` : ""} scoreClass="text-[#19E69B]" />
        <div className="flex flex-col items-center pt-12 text-[#B8D4C8]">
          <span className="text-[10px] font-black tracking-[0.35em]">VS</span>
          <span className="h-9 w-px bg-[#00C980]/30 mt-2" />
        </div>
        <LiveTeam name={match.teamB} logo={logoB} score={match.scoreB || "Yet to bat"} overs={match.scoreB ? "" : "Current innings"} scoreClass="text-[#F7FAF8]" />
      </div>

      {match.status === "live" && (match.matchData as any)?.chaseMessage && (
        <div className="mt-4 rounded-xl bg-[#0D4B34]/75 border border-[#00C980]/20 px-3 py-2 text-center text-sm font-semibold text-[#E7FFF4]">
          {(match.matchData as any).chaseMessage} <span className="text-[#19E69B]">→</span>
        </div>
      )}

      {match.venue && (
        <div className="mt-3 flex items-center justify-center gap-1.5 text-[#9FB6AC] text-xs truncate">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
          <span className="truncate">{match.venue}</span>
        </div>
      )}
    </button>
  );
}

function LiveTeam({ name, logo, score, overs, scoreClass }: { name: string | null; logo: string | null; score: string; overs: string; scoreClass: string }) {
  return (
    <div className="min-w-0 text-center flex flex-col items-center">
      <TeamLogo logo={logo} name={name} size={88} />
      <span className="text-[#F7FAF8] font-bold text-sm leading-tight mt-2 min-h-8 max-w-[130px] break-words">{name ?? "TBD"}</span>
      <span className={`${scoreClass} font-black text-3xl tabular-nums leading-none mt-2`}>{score}</span>
      <span className="text-[#9FB6AC] text-[11px] tabular-nums mt-1">{overs}</span>
    </div>
  );
}

function UpcomingTeam({ name, logo, align = "left" }: { name: string | null; logo: string | null; align?: "left" | "right" }) {
  return (
    <div className={`min-w-0 text-center flex flex-col items-center ${align === "right" ? "text-right" : "text-left"}`}>
      <TeamLogo logo={logo} name={name} size={76} />
      <span className="text-[#F7FAF8] font-bold text-sm leading-tight mt-2 min-h-8 max-w-[125px] break-words">{name ?? "TBD"}</span>
    </div>
  );
}

function ResultTeam({ name, logo, score, align = "left" }: { name: string | null; logo: string | null; score: string; align?: "left" | "right" }) {
  return (
    <div className={`min-w-0 text-center flex flex-col items-center ${align === "right" ? "text-right" : "text-left"}`}>
      <TeamLogo logo={logo} name={name} size={76} />
      <span className="text-[#F7FAF8] font-bold text-sm leading-tight mt-2 min-h-8 max-w-[125px] break-words">{name ?? "TBD"}</span>
      <span className="text-[#F7FAF8] font-black text-2xl tabular-nums mt-2">{score}</span>
    </div>
  );
}


/* ─── UPCOMING CARD ─── */

function UpcomingCard({ match }: { match: PublicMatch }) {
  const { logoA, logoB } = getTeamLogos(match);
  const { dateLine, timeLine } = formatUKDate(match.scheduledAt, (match.matchData as any)?.matchTime);

  return (
    <button
      onClick={() => match.matchData ? navigate(`/match/${match.id}`) : undefined}
      className="text-left w-full rounded-2xl p-4 sm:p-5 transition-all duration-200 active:scale-[0.985]"
      style={{ background: "rgba(6, 34, 25, 0.9)", border: "1px solid rgba(0,201,128,0.3)", boxShadow: "0 2px 12px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)", backdropFilter: "blur(12px)" }}
    >
      <div className="flex items-start justify-between mb-4">
        <span className="text-[#B8D4C8] text-[10px] font-bold px-2 py-1 rounded bg-white/[0.05] uppercase tracking-wider">{match.format || "Upcoming"}</span>
        <div className="text-right">
          <p className="text-[#F7FAF8] text-[11px] font-semibold leading-tight">{dateLine}</p>
          {timeLine && <p className="text-[#45B8FF] text-[11px] font-bold tabular-nums mt-0.5">{timeLine}</p>}
        </div>
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2">
        <UpcomingTeam name={match.teamA} logo={logoA} />
        <div className="pt-12 text-[#45B8FF] text-xs font-black tracking-wider">VS</div>
        <UpcomingTeam name={match.teamB} logo={logoB} align="right" />
      </div>

      {match.venue && (
        <div className="border-t border-white/[0.06] mt-4 pt-3 flex items-center justify-center gap-1.5 text-[#9FB6AC] text-xs truncate">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
          <span className="truncate">{match.venue}</span>
        </div>
      )}
    </button>
  );
}


/* ─── RESULT CARD ─── */

function ResultCard({ match }: { match: PublicMatch }) {
  const { logoA, logoB } = getTeamLogos(match);
  const resultSentence = getResultSentence(match);

  return (
    <button
      onClick={() => navigate(`/match/${match.id}`)}
      className="text-left w-full rounded-2xl p-4 sm:p-5 transition-all duration-200 active:scale-[0.985]"
      style={{ background: "rgba(6, 34, 25, 0.9)", border: "1px solid rgba(0,201,128,0.3)", boxShadow: "0 2px 12px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)", backdropFilter: "blur(12px)" }}
    >
      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2 mb-4">
        <ResultTeam name={match.teamA} logo={logoA} score={match.scoreA || "-"} />
        <div className="pt-12 text-[#D6B85A] text-[10px] font-black tracking-[0.3em]">VS</div>
        <ResultTeam name={match.teamB} logo={logoB} score={match.scoreB || "-"} align="right" />
      </div>

      {/* Result sentence */}
      {resultSentence && (
        <div className="border-t border-white/[0.06] pt-3">
          <p className="text-[#D6B85A] text-xs font-semibold truncate">{resultSentence}</p>
        </div>
      )}
    </button>
  );
}
